// src/components/Notification/withNotifications.tsx
import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_USER_NOTIFICATIONS,
  UPDATE_SEEN_NOTIFICATIONS,
} from "../../api/graphql/users";
import { NotificationContext } from "../../context/NotificationContext";
import NotificationSnackbar from "./NotificationSnackbar";
import { Notification } from "../../types/notification";

import { InfoIcon } from "../SVGcomponents/InfoIcon";

function withNotifications<T>(WrappedComponent: React.ComponentType<T>) {
  return function WithNotificationsWrapper(props: T) {
    const { data, loading, refetch } = useQuery<{
      getUserNotifications: Notification[];
    }>(GET_USER_NOTIFICATIONS, {
      notifyOnNetworkStatusChange: false,
      errorPolicy: "all",
    });

    const [updateSeenNotifications] = useMutation(UPDATE_SEEN_NOTIFICATIONS);
    const [queue, setQueue] = useState<Notification[]>([]);
    const [current, setCurrent] = useState<Notification | null>(null);
    const [progress, setProgress] = useState(100);
    const duration = 5000;

    const callNotification = useCallback(() => {
      refetch();
    }, [refetch]);

    const shouldShowAsSnackbar_old = (notification: Notification): boolean => {
      const msg = notification.message.toLowerCase();
      return /created|order|payment|ошибк|error|failed|оплата/i.test(msg);
    };

    // Backend notifications accumulate while the user is offline and must
    // only appear in the bell (NotificationModal), never as snackbars.
    const shouldShowAsSnackbar = (notification: Notification): boolean => {
      return false;
    };

    useEffect(() => {
      if (!loading && data?.getUserNotifications) {
        const allNew = data.getUserNotifications.filter(
          (notif) =>
            notif.id &&
            notif.isViewed === false &&
            !queue.some((q) => q.id === notif.id) &&
            (!current || notif.id !== current.id),
        );

        const snackbarNotifs = allNew.filter(shouldShowAsSnackbar);

        if (snackbarNotifs.length > 0) {
          setQueue((prev) => [...prev, ...snackbarNotifs]);
        }
      }
    }, [data, loading]);

    useEffect(() => {
      if (!current && queue.length > 0) {
        setCurrent(queue[0]);
        setQueue((q) => q.slice(1));
      }
    }, [queue, current]);

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (current) {
        setProgress(100);
        const start = Date.now();
        timer = setInterval(() => {
          const elapsed = Date.now() - start;
          const newProgress = Math.max(0, 100 - (elapsed / duration) * 100);
          setProgress(newProgress);
          if (newProgress === 0) {
            handleClose();
          }
        }, 30);
      }
      return () => clearInterval(timer);
    }, [current]);

    useEffect(() => {
      if (current?.id) {
        updateSeenNotifications({
          variables: { notificationIds: [current.id] },
        });
      }
    }, [current]);

    const handleClose = () => {
      setCurrent(null);
    };

    const getNotificationIcon = (notif: Notification) => {
      return {
        icon: <InfoIcon />,
        bg: "#6a36ff",
      };
    };

    return (
      <NotificationContext.Provider value={{ callNotification }}>
        <WrappedComponent {...props} />
        {current && (
          <NotificationSnackbar
            message={current.message}
            icon={getNotificationIcon(current).icon}
            bg={getNotificationIcon(current).bg}
            onClose={handleClose}
            progress={progress}
          />
        )}
      </NotificationContext.Provider>
    );
  };
}

export default withNotifications;
